// Pipeline-board-specific mapping from lifecycle.ts's UI-mirror predicates
// to concrete drag-and-drop / keyboard actions on a quote card. Kept
// separate from lifecycle.ts (which stays a pure, column-agnostic mirror of
// internal/lifecycle) because "which board column does this transition land
// in" is a pipeline-board concept, not a lifecycle one.
//
// The board only has 4 columns (draft/submitted/priced/revised) — approved
// is a terminal state and lives in the separate Approved list, not a
// column, so `approve` never has a targetCol: a card can never be *dropped*
// onto Approved, only actioned via the keyboard-reachable select on the
// card (see QuoteCard.svelte).
import type { Quotation, QuotationStatus } from '$lib/types';
import * as lifecycle from '$lib/utils/lifecycle';

export type ColKey = 'draft' | 'submitted' | 'priced' | 'revised';

export type ActionId = 'submit' | 'price-navigate' | 'approve' | 'return-for-pricing';

export interface LegalAction {
  id: ActionId;
  label: string;
  // Present only when a board column represents this transition's
  // destination — i.e. only these are legal *drop* targets. `approve` has
  // none: it's keyboard/select-only, since Approved isn't a board column.
  targetCol?: ColKey;
  confirm?: string;
}

type Q = Pick<Quotation, 'status' | 'created_by_id' | 'designer_priced'>;

export function columnKeyForStatus(status: QuotationStatus): ColKey | null {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'submitted':
      return 'submitted';
    case 'priced':
      return 'priced';
    case 'revised':
      return 'revised';
    default:
      return null; // 'approved' has no board column
  }
}

// legalActions lists every lifecycle action this user may legally perform
// on this quote right now, reusing lifecycle.ts's predicates verbatim (the
// server is still the real gate — see quotations.go handlers — this only
// decides what the board offers).
export function legalActions(quote: Q, userID: number | undefined, isAdmin: boolean): LegalAction[] {
  const out: LegalAction[] = [];
  const isOwnerOrAdmin = isAdmin || quote.created_by_id === userID;

  if (lifecycle.canSubmit(quote.status) && isOwnerOrAdmin) {
    out.push({ id: 'submit', label: 'Submit for pricing', targetCol: 'submitted' });
  }
  // Mirrors the "Save pricing" button gate in QuoteActionsSummary.svelte
  // exactly (admin, non-self-serve, canPrice) — see PipelineBoard.svelte's
  // handling of 'price-navigate' for why this navigates instead of firing
  // POST /price with no payload.
  if (isAdmin && !quote.designer_priced && lifecycle.canPrice(quote.status)) {
    out.push({ id: 'price-navigate', label: 'Open to add pricing', targetCol: 'priced' });
  }
  if (lifecycle.canApprove(quote, userID, isAdmin)) {
    out.push({
      id: 'approve',
      label: 'Approve',
      confirm:
        'Approving generates the client PDF and locks this quotation into a new version. It will move off the pipeline board into Approved. Continue?'
    });
  }
  if (lifecycle.canReturnForPricing(quote.status) && lifecycle.manageableBy(quote, userID, isAdmin)) {
    out.push({
      id: 'return-for-pricing',
      label: 'Return for pricing edits',
      targetCol: 'revised',
      confirm: 'This sends the quotation back to the designer for pricing edits. Continue?'
    });
  }
  return out;
}
